import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatesApi, complianceStandardsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { SlideDrawer } from '../components/ui/slide-drawer';
import {
  Loader2, Plus, Trash2, ClipboardList, Camera, Search, X, Check, Clock,
  ChevronRight, Send, Pencil, Copy, Shield, Info, FileUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { useConfirm } from '../components/useConfirm';

// ── Model ────────────────────────────────────────────────────────────
const isDraft = (t) => (t?.status || 'published') === 'draft';
const STATUS = {
  published: { label: 'Live', text: '#047857', fill: '#ECFDF5', border: '#A7F3D0', dot: '#10B981' },
  draft: { label: 'Draft', text: '#8A6D00', fill: '#FFF8D6', border: '#EFE0A0', dot: '#E0B800' },
};
const TYPE_META = {
  pass_fail: { label: 'Pass / fail', text: '#3D3A35', fill: '#F6F4EF', border: '#DAD5CA' },
  hours: { label: 'Machine hours', text: '#8A6D00', fill: '#FFF8D6', border: '#EFE0A0' },
  odometer: { label: 'Odometer', text: '#8A6D00', fill: '#FFF8D6', border: '#EFE0A0' },
  number: { label: 'Number', text: '#3D3A35', fill: '#F6F4EF', border: '#DAD5CA' },
  text: { label: 'Text', text: '#3D3A35', fill: '#F6F4EF', border: '#DAD5CA' },
  select: { label: 'Select', text: '#3D3A35', fill: '#F6F4EF', border: '#DAD5CA' },
  signature: { label: 'Signature', text: '#047857', fill: '#ECFDF5', border: '#A7F3D0' },
};
const TYPE_ORDER = ['pass_fail', 'hours', 'odometer', 'number', 'text', 'select', 'signature'];
const TYPE_HINT = {
  hours: 'Writes the reading back to the asset record on submit.',
  odometer: 'Writes the reading back to the asset record on submit.',
  number: 'Add a unit so the mechanic knows what to enter.',
  select: 'Comma-separated options, e.g. Pit A, Pit B, Yard.',
  text: "Can pre-fill with the mechanic's name or email.",
  signature: 'Renders a signature pad, not a pass/fail control.',
};
const photoCount = (t) => (t.checklist || []).filter(i => i.photo_required).length;
const requiredCount = (t) => (t.checklist || []).filter(i => i.required).length;
const relTime = (iso) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

// ── Atoms ────────────────────────────────────────────────────────────
const StatusPill = ({ template }) => {
  const m = STATUS[isDraft(template) ? 'draft' : 'published'];
  return (
    <span className="inline-flex items-center gap-1.5 uppercase font-semibold" style={{ fontSize: 11, letterSpacing: '0.04em', color: m.text, background: m.fill, border: `1px solid ${m.border}`, borderRadius: 999, padding: '2px 9px' }} data-testid={`template-status-badge-${template.id}`}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: m.dot }} />{m.label} · v{template.version || 1}
    </span>
  );
};
const TypeChip = ({ type }) => {
  const m = TYPE_META[type] || TYPE_META.pass_fail;
  return <span className="font-semibold" style={{ fontSize: 11, color: m.text, background: m.fill, border: `1px solid ${m.border}`, borderRadius: 999, padding: '2px 9px' }}>{m.label}</span>;
};
const FilterPills = ({ options, value, onChange, testId }) => (
  <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap" data-testid={testId}>
    {options.map(o => {
      const active = value === o.id;
      return (
        <button key={o.id} type="button" onClick={() => onChange(o.id)} data-testid={o.testId}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
          {o.dot && <span className="w-2 h-2 rounded-full" style={{ background: o.dot }} />}
          {o.label}
          {o.count != null && <span className={`text-[11px] font-bold ${active ? 'text-black/70' : 'text-muted-foreground'}`}>{o.count}</span>}
        </button>
      );
    })}
  </div>
);

// ── Create / Edit modal ──────────────────────────────────────────────
const TemplateFormModal = ({ existing, onSuccess, onClose }) => {
  const isEdit = !!existing;
  const publishedEdit = isEdit && !isDraft(existing);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: existing?.name || '', description: existing?.description || '', category: existing?.category || '', cadence: existing?.cadence || 'ad_hoc' });
  const [checklist, setChecklist] = useState(
    (existing?.checklist && existing.checklist.length > 0)
      ? existing.checklist.map(it => ({ ...it, _id: crypto.randomUUID(), item_type: it.item_type || 'pass_fail' }))
      : [{ _id: crypto.randomUUID(), question: '', required: true, photo_required: false, item_type: 'pass_fail' }]
  );
  const addItem = () => setChecklist(c => [...c, { _id: crypto.randomUUID(), question: '', required: true, photo_required: false, item_type: 'pass_fail' }]);
  const removeItem = (id) => setChecklist(c => c.length > 1 ? c.filter(i => i._id !== id) : c);
  const updateItem = (id, field, value) => setChecklist(c => c.map(it => {
    if (it._id !== id) return it;
    const next = { ...it, [field]: value };
    if (field === 'item_type') {
      next.unit = undefined; next.options = undefined; next.updates_asset_field = undefined; next.prefill_user_field = undefined;
      if (value === 'hours') next.updates_asset_field = 'hours';
      if (value === 'odometer') next.updates_asset_field = 'odometer_km';
    }
    return next;
  }));
  const ready = checklist.filter(i => i.question.trim()).length;

  const submit = async (e) => {
    e.preventDefault();
    const validItems = checklist.filter(i => i.question.trim()).map(({ _id, ...rest }) => rest);
    if (validItems.length === 0) { toast.error('Add at least one question'); return; }
    setSubmitting(true);
    try {
      if (isEdit) { await templatesApi.update(existing.id, { ...form, checklist: validItems }); toast.success(publishedEdit ? `Saved — published version ${(existing.version || 1) + 1}` : 'Draft saved'); }
      else { await templatesApi.create({ ...form, checklist: validItems }); toast.success('Saved as a draft'); }
      onSuccess();
    } catch { toast.error(isEdit ? 'Failed to update template' : 'Failed to create template'); }
    finally { setSubmitting(false); }
  };

  const consequence = publishedEdit
    ? `Saving publishes version ${(existing.version || 1) + 1}. Inspections already completed keep the version they were run against.`
    : 'Saved as a draft. Mechanics see it only once you publish.';

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(26,26,26,0.45)' }} onClick={onClose} data-testid="template-form-modal">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="w-full max-w-[660px] mt-[5vh] flex flex-col" style={{ background: '#FBFAF7', border: '1px solid #DAD5CA', borderRadius: 14, boxShadow: '0 30px 70px -30px rgba(26,26,26,0.55)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E2D9]">
          <h2 className="font-extrabold uppercase tracking-tight" style={{ fontSize: 14, fontFamily: 'Manrope, sans-serif' }}>{isEdit ? 'Edit template' : 'Create template'}</h2>
          <button type="button" onClick={onClose} className="text-[#8F8A7F] hover:text-[#1A1A1A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto" style={{ maxHeight: '72vh' }}>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)' }}>
            <div>
              <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Template name</label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Daily Pre-Start Check" required data-testid="template-name-input" className="h-10 border-[#C9C3B6] rounded-lg" />
            </div>
            <div>
              <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Category</label>
              <Input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Heavy Equipment" data-testid="template-category-input" className="h-10 border-[#C9C3B6] rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Description</label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="What this checklist is for, and when to run it" rows={2} data-testid="template-description-input" className="border-[#C9C3B6] rounded-lg" />
          </div>

          <div>
            <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-1.5" style={{ fontSize: 11, fontWeight: 700 }}>Inspection type</label>
            <div className="flex flex-wrap gap-2" data-testid="template-cadence">
              {[
                { v: 'prestart', label: 'Daily pre-start', help: 'Excluded from readiness — a failed item raises a defect' },
                { v: 'periodic', label: 'Periodic', help: 'Drives the overdue-inspection readiness check' },
                { v: 'ad_hoc', label: 'Ad-hoc', help: "Doesn't affect readiness" },
              ].map(o => (
                <button key={o.v} type="button" onClick={() => setForm(p => ({ ...p, cadence: o.v }))}
                  title={o.help} data-testid={`cadence-${o.v}`}
                  className={`px-3 py-1.5 rounded-lg border text-[12.5px] font-semibold transition-colors ${form.cadence === o.v ? 'border-[#C9A400] bg-[#FFFBEA] text-[#8A6D00]' : 'border-[#C9C3B6] text-[#6B6660] hover:border-[#C9A400]'}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <p className="text-[#8F8A7F] mt-1.5" style={{ fontSize: 11.5 }}>
              {form.cadence === 'periodic'
                ? 'Set a "Periodic inspection (days)" per category in Settings → Readiness to flag assets Red when overdue.'
                : form.cadence === 'prestart'
                ? 'Daily pre-starts never affect yard readiness directly; a failed check raises a defect instead.'
                : 'Ad-hoc checklists are informational only.'}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Checklist items <span className="font-mono text-[#8F8A7F] ml-1">{checklist.length}</span></span>
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-[30px]" data-testid="add-item-btn"><Plus className="w-4 h-4 mr-1" />Add item</Button>
            </div>
            <div className="space-y-2.5">
              {checklist.map((item, index) => (
                <div key={item._id} className="bg-white" style={{ border: '1px solid #DAD5CA', borderRadius: 10, padding: '11px 12px' }}>
                  <div className="flex items-center gap-2.5">
                    <span className="font-mono shrink-0" style={{ fontSize: 12, color: '#B5AFA3' }}>{index + 1}</span>
                    <Input value={item.question} onChange={e => updateItem(item._id, 'question', e.target.value)} placeholder="Enter inspection question…" className="h-[38px] flex-1 border-[#C9C3B6] rounded-lg" data-testid={`checklist-question-${index}`} />
                    {checklist.length > 1 && <button type="button" onClick={() => removeItem(item._id)} className="w-8 h-8 shrink-0 rounded-md flex items-center justify-center text-[#8F8A7F] hover:text-[#B91C1C] hover:bg-[#FDECEC]" data-testid={`remove-item-${index}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  <div className="mt-2.5 pl-[22px] flex flex-wrap items-center gap-2">
                    <span className="uppercase tracking-[0.08em] text-[#8F8A7F] mr-1" style={{ fontSize: 10, fontWeight: 700 }}>Answer</span>
                    <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap" data-testid={`item-type-${index}`}>
                      {TYPE_ORDER.map(t => {
                        const active = (item.item_type || 'pass_fail') === t;
                        return <button key={t} type="button" onClick={() => updateItem(item._id, 'item_type', t)} className={`px-2.5 py-1 rounded-md text-[12px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{TYPE_META[t].label}</button>;
                      })}
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <ToggleChip checked={item.required} onClick={() => updateItem(item._id, 'required', !item.required)} label="Required" />
                      <ToggleChip checked={item.photo_required} onClick={() => updateItem(item._id, 'photo_required', !item.photo_required)} label="Photo" icon={Camera} />
                    </div>
                  </div>
                  {TYPE_HINT[item.item_type] && (
                    <div className="mt-2 pl-[22px] space-y-2">
                      <p className="text-[#6B6660]" style={{ fontSize: 12 }}>{TYPE_HINT[item.item_type]}</p>
                      {item.item_type === 'number' && <Input value={item.unit || ''} onChange={e => updateItem(item._id, 'unit', e.target.value)} placeholder="Unit (e.g. %, litres, psi)" className="h-8 max-w-[220px] border-[#C9C3B6] rounded-lg text-[13px]" />}
                      {item.item_type === 'select' && <Input value={(item.options || []).join(', ')} onChange={e => updateItem(item._id, 'options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Pit A, Pit B, Yard" className="h-8 border-[#C9C3B6] rounded-lg text-[13px]" />}
                      {item.item_type === 'text' && (
                        <select value={item.prefill_user_field || ''} onChange={e => updateItem(item._id, 'prefill_user_field', e.target.value || null)} className="h-8 px-2 rounded-lg border border-[#C9C3B6] bg-white text-[13px]">
                          <option value="">No auto-prefill</option><option value="name">Pre-fill with user's name</option><option value="email">Pre-fill with user's email</option>
                        </select>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-start gap-2 text-[#3D3A35]" style={{ padding: '11px 13px', borderRadius: 9, fontSize: 12.5, background: '#F6F4EF', border: '1px solid #E2DED4' }}>
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-[#6B6660]" />{consequence}
          </div>
        </div>
        <div className="flex items-center gap-3 px-5 py-3.5 border-t border-[#DAD5CA] bg-white rounded-b-[14px]">
          <Button type="submit" disabled={submitting || ready === 0} className="btn-industrial h-[38px]" style={{ width: 150 }} data-testid="submit-template-btn">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEdit ? 'Save changes' : 'Save draft'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} className="h-[38px]">Cancel</Button>
          <span className="ml-auto text-[#8F8A7F]" style={{ fontSize: 12 }}>{ready === 0 ? 'Add at least one question' : `${ready} question${ready === 1 ? '' : 's'} ready`}</span>
        </div>
      </form>
    </div>
  );
};

const ToggleChip = ({ checked, onClick, label, icon: Icon }) => (
  <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 transition-colors" style={{ height: 28, padding: '0 10px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, color: checked ? '#8A6D00' : '#6B6660', background: checked ? '#FFFDF2' : '#fff', border: `1px solid ${checked ? '#E0B800' : '#E2DED4'}` }}>
    <span className="flex items-center justify-center" style={{ width: 16, height: 16, borderRadius: 5, background: checked ? '#FFD108' : '#fff', border: `1px solid ${checked ? '#E0B800' : '#C9C3B6'}` }}>{checked && <Check className="w-3 h-3 text-black" strokeWidth={3} />}</span>
    {Icon && <Icon className="w-3.5 h-3.5" />}{label}
  </button>
);

// ── Detail drawer body ───────────────────────────────────────────────
const DetailBody = ({ template, formatDate, onEdit, onDuplicate, onPublish, onDelete }) => {
  const draft = isDraft(template);
  const sm = STATUS[draft ? 'draft' : 'published'];
  const checklist = template.checklist || [];
  return (
    <div className="space-y-3.5">
      {/* Title block */}
      <div>
        <h2 className="font-bold" style={{ fontSize: 19, fontFamily: 'Manrope, sans-serif' }}>{template.name}</h2>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {template.category && <span className="uppercase font-semibold" style={{ fontSize: 11.5, color: '#6B6660', background: '#F6F4EF', border: '1px solid #DAD5CA', borderRadius: 999, padding: '2px 9px' }}>{template.category}</span>}
          {template.description && <span className="text-[#6B6660]" style={{ fontSize: 12.5 }}>{template.description}</span>}
        </div>
      </div>

      {/* Status strip */}
      <div className="flex items-center justify-between" style={{ padding: '10px 14px', borderRadius: 10, background: sm.fill, border: `1px solid ${sm.border}`, borderLeft: `3px solid ${sm.dot}` }}>
        <span className="font-semibold flex items-center gap-2" style={{ fontSize: 13, color: sm.text }}><span style={{ width: 6, height: 6, borderRadius: 999, background: sm.dot }} />{draft ? 'Draft — not visible to mechanics' : 'Published — live in the field'}</span>
        <span className="font-mono text-[#6B6660]" style={{ fontSize: 12 }}>Updated {formatDate(template.updated_at)}</span>
      </div>

      {/* Provenance */}
      {template.source_standard_code && (
        <div style={{ padding: '12px 14px', borderRadius: 10, background: '#fff', border: '1px dashed #C9C3B6' }}>
          <p className="font-semibold text-[#1A1A1A] flex items-center gap-2" style={{ fontSize: 13 }}><Shield className="w-4 h-4 text-[#8A6D00]" />Generated from {template.source_standard_code}</p>
          <p className="text-[#6B6660] mt-1" style={{ fontSize: 12 }}>One item per component type in the standard, plus hours and notes. Edit freely — publishing does not send anything back to the standard.</p>
        </div>
      )}

      {/* Checklist */}
      <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
        <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9]">
          <span className="uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>Checklist <span className="font-mono ml-1 text-[#8F8A7F]">{checklist.length} items</span></span>
          <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>{requiredCount(template)} required · {photoCount(template)} need a photo</span>
        </div>
        {checklist.map((item, i) => (
          <div key={item.id || i} className="grid border-b border-[#F4F2ED] last:border-0" style={{ gridTemplateColumns: '26px 1fr', padding: '10px 14px' }}>
            <span className="font-mono" style={{ fontSize: 12, color: '#B5AFA3' }}>{i + 1}</span>
            <div>
              <p className="text-[#1A1A1A]" style={{ fontSize: 13.5 }}>{item.question}</p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <TypeChip type={item.item_type || 'pass_fail'} />
                {item.required && <span className="inline-flex items-center gap-1 font-medium" style={{ fontSize: 11, color: '#3D3A35', background: '#F6F4EF', border: '1px solid #DAD5CA', borderRadius: 999, padding: '2px 8px' }}><Check className="w-3 h-3" strokeWidth={3} />Required</span>}
                {item.photo_required && <span className="inline-flex items-center gap-1 font-medium" style={{ fontSize: 11, color: '#8A6D00', background: '#FFF8D6', border: '1px solid #EFE0A0', borderRadius: 999, padding: '2px 8px' }}><Camera className="w-3 h-3" />Photo</span>}
                {item.updates_asset_field && <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>Updates asset {item.item_type === 'odometer' ? 'odometer' : 'hours'} on submit</span>}
                {item.unit && <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>Unit: {item.unit}</span>}
                {item.options?.length > 0 && <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>{item.options.join(', ')}</span>}
                {item.prefill_user_field && <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>Pre-filled with user's {item.prefill_user_field}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Version history */}
      <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
        <div className="px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9] uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>Version history</div>
        <div className="flex items-center gap-3" style={{ padding: '10px 14px' }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: sm.dot }} />
          <span className="flex-1 text-[#1A1A1A]" style={{ fontSize: 12.5 }}>{draft ? 'Draft created' : `Version ${template.version || 1} live`}<span className="block font-mono text-[#8F8A7F]" style={{ fontSize: 10.5 }}>{formatDate(template.updated_at || template.created_at)}</span></span>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 pt-3.5 border-t border-[#DAD5CA]">
        {draft ? (
          <>
            <Button onClick={onPublish} className="btn-industrial h-[38px]" data-testid="drawer-publish-template-btn"><Send className="w-4 h-4 mr-1.5" />Publish to mechanics</Button>
            <Button variant="outline" onClick={onEdit} className="h-[38px]" data-testid="drawer-edit-template-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit</Button>
          </>
        ) : (
          <Button onClick={onEdit} className="btn-industrial h-[38px]" data-testid="drawer-edit-template-btn"><Pencil className="w-4 h-4 mr-1.5" />Edit — creates v{(template.version || 1) + 1}</Button>
        )}
        <Button variant="outline" onClick={onDuplicate} className="h-[38px]" data-testid="drawer-duplicate-template-btn"><Copy className="w-4 h-4 mr-1.5" />Duplicate</Button>
        <Button variant="outline" onClick={onDelete} className="h-[38px] ml-auto" style={{ color: '#B91C1C', borderColor: '#E3B5AF' }} data-testid="drawer-delete-template-btn"><Trash2 className="w-4 h-4 mr-1.5" />Delete</Button>
      </div>
    </div>
  );
};

// ── Generate from standard ───────────────────────────────────────────
const GenerateModal = ({ onClose, onGenerated }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [standards, setStandards] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [cadence, setCadence] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => { try { const r = await complianceStandardsApi.list(); setStandards(r.data || []); } catch { toast.error('Failed to load standards'); } finally { setLoading(false); } })();
  }, []);
  const selected = useMemo(() => standards.find(s => s.id === selectedId), [standards, selectedId]);
  useEffect(() => {
    if (!selected) { setCadence(''); setCategory(''); return; }
    setCadence((selected.required_inspections || [])[0]?.type || '');
    setCategory((selected.asset_categories || [])[0] || '');
  }, [selectedId]); // eslint-disable-line

  const components = selected?.component_types || [];
  const generate = async () => {
    if (!selectedId) { toast.error('Select a compliance standard'); return; }
    setSubmitting(true);
    try {
      const res = await complianceStandardsApi.generateTemplate(selectedId, { inspection_type: cadence || undefined, category: category || undefined });
      toast.success(`Draft created — "${res.data?.template?.name || 'Template'}"`);
      onGenerated?.(res.data?.template);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate template'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(26,26,26,0.45)' }} onClick={onClose} data-testid="generate-template-dialog">
      <div onClick={e => e.stopPropagation()} className="w-full max-w-[520px] mt-[5vh] flex flex-col" style={{ background: '#FBFAF7', border: '1px solid #DAD5CA', borderRadius: 14, boxShadow: '0 30px 70px -30px rgba(26,26,26,0.55)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E2D9]">
          <h2 className="font-extrabold uppercase tracking-tight" style={{ fontSize: 14, fontFamily: 'Manrope, sans-serif' }}>Generate from standard</h2>
          <button type="button" onClick={onClose} className="text-[#8F8A7F] hover:text-[#1A1A1A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto" style={{ maxHeight: '74vh' }}>
          <p className="text-[#6B6660]" style={{ fontSize: 12.5 }}>Builds a draft checklist from a compliance standard's component types. Nothing reaches your mechanics until you review it and publish.</p>
          {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
            <>
              <div>
                <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-2" style={{ fontSize: 11, fontWeight: 700 }}>Compliance standard</label>
                {standards.length === 0 ? (
                  <p className="text-[#8F8A7F]" style={{ fontSize: 12.5 }}>No standards in your library yet. <button className="text-[#8A6D00] underline" onClick={() => navigate('/compliance')}>Open Compliance</button> to seed or import.</p>
                ) : (
                  <div className="space-y-2">
                    {standards.map(s => {
                      const sel = s.id === selectedId;
                      return (
                        <button key={s.id} type="button" onClick={() => setSelectedId(s.id)} data-testid={`generate-standard-${s.id}`} className="w-full flex items-center gap-3 text-left transition-colors" style={{ padding: '10px 12px', borderRadius: 9, background: sel ? '#FFFDF2' : '#fff', border: `1px solid ${sel ? '#E0B800' : '#E2DED4'}` }}>
                          <span className="font-mono shrink-0" style={{ fontSize: 11, color: '#3D3A35', background: '#F6F4EF', border: '1px solid #DAD5CA', borderRadius: 5, padding: '2px 6px' }}>{s.code}</span>
                          <span className="flex-1 text-[#1A1A1A]" style={{ fontSize: 13 }}>{s.name}</span>
                          <span className="text-[#8F8A7F] shrink-0" style={{ fontSize: 11.5 }}>{(s.component_types || []).length} components</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {selected && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-2" style={{ fontSize: 11, fontWeight: 700 }}>Cadence</label>
                      {(selected.required_inspections || []).length <= 1 ? (
                        <span className="inline-flex font-semibold" style={{ fontSize: 12.5, color: '#3D3A35', background: '#F6F4EF', border: '1px solid #DAD5CA', borderRadius: 999, padding: '4px 12px' }}>{(selected.required_inspections || [])[0]?.type || 'Inspection'}</span>
                      ) : (
                        <FilterPills value={cadence} onChange={setCadence} testId="generate-cadence" options={(selected.required_inspections).map(ri => ({ id: ri.type, label: ri.type }))} />
                      )}
                    </div>
                    <div>
                      <label className="block uppercase tracking-[0.08em] text-[#6B6660] mb-2" style={{ fontSize: 11, fontWeight: 700 }}>Asset category</label>
                      {(selected.asset_categories || []).length <= 1 ? (
                        <span className="inline-flex font-semibold" style={{ fontSize: 12.5, color: '#3D3A35', background: '#F6F4EF', border: '1px solid #DAD5CA', borderRadius: 999, padding: '4px 12px' }}>{(selected.asset_categories || [])[0] || 'custom'}</span>
                      ) : (
                        <FilterPills value={category} onChange={setCategory} testId="generate-category" options={(selected.asset_categories).map(c => ({ id: c, label: c }))} />
                      )}
                    </div>
                  </div>

                  <div style={{ border: '1px solid #E6E2D9', borderRadius: 10, overflow: 'hidden' }}>
                    <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#F6F4EF] border-b border-[#E6E2D9]">
                      <span className="uppercase tracking-[0.09em] text-[#6B6660]" style={{ fontSize: 10.5, fontWeight: 700 }}>Preview</span>
                      <span className="font-mono text-[#8F8A7F]" style={{ fontSize: 11 }}>{components.length + 2} items</span>
                    </div>
                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                      {components.map((c, i) => (
                        <div key={i} className="flex items-center gap-3 border-b border-[#F4F2ED]" style={{ padding: '8px 14px' }}>
                          <span className="font-mono" style={{ fontSize: 11, color: '#B5AFA3' }}>{i + 1}</span>
                          <span className="flex-1 text-[#1A1A1A]" style={{ fontSize: 12.5 }}>{c} — condition check</span>
                          <TypeChip type="pass_fail" />
                        </div>
                      ))}
                      <div className="flex items-center gap-3 border-b border-[#F4F2ED]" style={{ padding: '8px 14px' }}>
                        <span className="font-mono" style={{ fontSize: 11, color: '#B5AFA3' }}>{components.length + 1}</span>
                        <span className="flex-1 text-[#1A1A1A]" style={{ fontSize: 12.5 }}>Machine hours</span><TypeChip type="hours" />
                      </div>
                      <div className="flex items-center gap-3" style={{ padding: '8px 14px' }}>
                        <span className="font-mono" style={{ fontSize: 11, color: '#B5AFA3' }}>{components.length + 2}</span>
                        <span className="flex-1 text-[#1A1A1A]" style={{ fontSize: 12.5 }}>Inspector notes</span><TypeChip type="text" />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-3 px-5 py-3.5 border-t border-[#DAD5CA] bg-white rounded-b-[14px]">
          <Button onClick={generate} disabled={!selectedId || submitting} className="btn-industrial h-[38px]" style={{ width: 160 }} data-testid="generate-template-submit-btn">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Generate draft
          </Button>
          <Button variant="outline" onClick={onClose} className="h-[38px]">Cancel</Button>
          <span className="ml-auto text-[#8F8A7F]" style={{ fontSize: 12 }}>Saved as a draft</span>
        </div>
      </div>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────
export const TemplatesListPage = () => {
  const { confirm, dialogProps } = useConfirm();
  const { formatDate } = useSettings();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sort, setSort] = useState('updated');
  const [drawer, setDrawer] = useState(null); // template
  const [modal, setModal] = useState(null); // {existing} or {}
  const [generateOpen, setGenerateOpen] = useState(false);
  const { isOfficeUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadTemplates(); }, []);
  const loadTemplates = async (openId) => {
    try { const r = await templatesApi.getAll(); setTemplates(r.data); if (openId) { const t = (r.data || []).find(x => x.id === openId); if (t) setDrawer(t); } }
    catch { toast.error('Failed to load templates'); } finally { setLoading(false); }
  };

  const drafts = useMemo(() => templates.filter(isDraft), [templates]);
  const categories = useMemo(() => [...new Set(templates.map(t => t.category).filter(Boolean))], [templates]);
  const statusCounts = { all: templates.length, published: templates.filter(t => !isDraft(t)).length, draft: drafts.length };
  const catCount = (c) => templates.filter(t => t.category === c).length;

  const filtered = useMemo(() => {
    let list = templates.filter(t => {
      const q = search.toLowerCase();
      const matchesSearch = !q || t.name?.toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q) || (t.checklist || []).some(i => (i.question || '').toLowerCase().includes(q));
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'draft' ? isDraft(t) : !isDraft(t));
      const matchesCat = categoryFilter === 'all' || t.category === categoryFilter;
      return matchesSearch && matchesStatus && matchesCat;
    });
    list.sort((a, b) => sort === 'name' ? (a.name || '').localeCompare(b.name || '') : new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
    return list;
  }, [templates, search, statusFilter, categoryFilter, sort]);

  const handleDelete = (t) => confirm({
    title: 'Delete this template?', description: `"${t.name}" will be permanently removed.`,
    footnote: 'Past inspections keep their snapshot and are unaffected.', testId: 'confirm-delete-inspection-template',
    onConfirm: async () => { try { await templatesApi.delete(t.id); toast.success('Template deleted'); setDrawer(null); loadTemplates(); } catch (e) { toast.error('Failed to delete template'); throw e; } },
  });
  const handlePublish = async (t) => { try { await templatesApi.publish(t.id); toast.success(`"${t.name}" published — mechanics can now use it.`); setDrawer(null); loadTemplates(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to publish'); } };
  const handleDuplicate = async (t) => {
    try {
      const items = (t.checklist || []).map(({ id, ...rest }) => rest);
      const res = await templatesApi.create({ name: `${t.name} (copy)`, description: t.description || '', category: t.category || '', checklist: items });
      toast.success('Duplicated as a new draft'); setDrawer(null); loadTemplates(res.data?.id);
    } catch { toast.error('Failed to duplicate'); }
  };

  if (!isOfficeUser()) { navigate('/dashboard'); return null; }
  const hasFilters = search || statusFilter !== 'all' || categoryFilter !== 'all';

  return (
    <div className="flex flex-col gap-6 animate-fade-in" data-testid="templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Inspection Templates</h1>
          <p className="text-muted-foreground">The checklists your mechanics see in the field. Drafts stay hidden until published.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setGenerateOpen(true)} variant="outline" data-testid="generate-from-standard-btn"><FileUp className="w-4 h-4 mr-2" />From standard</Button>
          <Button onClick={() => setModal({})} className="btn-industrial" data-testid="create-template-btn"><Plus className="w-5 h-5 mr-2" />Create template</Button>
        </div>
      </div>

      {/* Draft banner */}
      {drafts.length > 0 && (
        <div className="flex items-center justify-between gap-3" style={{ background: '#FFF8D6', border: '1px solid #EFE0A0', borderLeft: '3px solid #E0B800', borderRadius: 10, padding: '11px 14px' }} data-testid="draft-banner">
          <span className="flex items-center gap-2 text-[#6B5500]" style={{ fontSize: 12.5 }}>
            <Clock className="w-4 h-4 shrink-0" />
            {drafts.length === 1 ? `1 draft is not visible to mechanics yet — "${drafts[0].name}".` : `${drafts.length} drafts are not visible to mechanics yet.`}
          </span>
          <Button variant="outline" size="sm" className="h-[30px] shrink-0" onClick={() => setStatusFilter('draft')} data-testid="review-drafts-btn">Review drafts</Button>
        </div>
      )}

      {/* Register */}
      <div className="bg-white overflow-hidden" style={{ border: '1px solid #DAD5CA', borderRadius: 12, boxShadow: '0 1px 2px rgba(26,26,26,0.05), 0 8px 20px -14px rgba(26,26,26,0.16)' }}>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3" style={{ padding: '13px 14px', borderBottom: '1px solid #E6E2D9' }}>
          <div className="relative w-full lg:max-w-[400px] lg:min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8F8A7F]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Template, category or question text" data-testid="template-search-input" className="pl-10 pr-14 h-[42px] border-[1.5px] border-[#1A1A1A] rounded-[9px]" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[#8F8A7F]" style={{ fontSize: 10.5 }}>{filtered.length}/{templates.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Sort</span>
            <FilterPills value={sort} onChange={setSort} testId="template-sort" options={[{ id: 'updated', label: 'Updated', testId: 'sort-updated' }, { id: 'name', label: 'Name', testId: 'sort-name' }]} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" style={{ padding: '11px 14px', borderBottom: '1px solid #E6E2D9' }}>
          <span className="uppercase tracking-[0.08em] text-[#6B6660]" style={{ fontSize: 11, fontWeight: 700 }}>Status</span>
          <FilterPills value={statusFilter} onChange={setStatusFilter} testId="status-filter" options={[
            { id: 'all', label: 'All', count: statusCounts.all, testId: 'status-all' },
            { id: 'published', label: 'Published', count: statusCounts.published, dot: STATUS.published.dot, testId: 'status-published' },
            { id: 'draft', label: 'Draft', count: statusCounts.draft, dot: STATUS.draft.dot, testId: 'status-draft' },
          ]} />
          {categories.length > 0 && <>
            <span className="uppercase tracking-[0.08em] text-[#6B6660] ml-2" style={{ fontSize: 11, fontWeight: 700 }}>Category</span>
            <FilterPills value={categoryFilter} onChange={setCategoryFilter} testId="category-filter" options={[{ id: 'all', label: 'All', count: templates.length, testId: 'cat-all' }, ...categories.map(c => ({ id: c, label: c, count: catCount(c), testId: `cat-${c}` }))]} />
          </>}
          {hasFilters && <button onClick={() => { setSearch(''); setStatusFilter('all'); setCategoryFilter('all'); }} className="text-[#8F8A7F] hover:text-[#1A1A1A] flex items-center gap-1" style={{ fontSize: 12 }} data-testid="clear-filters-btn"><X className="w-3.5 h-3.5" />Clear filters</button>}
        </div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          : filtered.length === 0 ? (
            <div className="py-16 text-center"><ClipboardList className="w-12 h-12 mx-auto text-[#C9C3B6] mb-3" /><h3 className="font-bold text-[#1A1A1A]" style={{ fontSize: 16 }}>No templates found</h3><p className="text-[#8F8A7F]" style={{ fontSize: 12.5 }}>No checklists match the current filters.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <div style={{ minWidth: 1020 }}>
                <div className="grid items-center bg-[#F6F4EF] uppercase tracking-[0.1em] text-[#6B6660]" style={{ gridTemplateColumns: 'minmax(250px,1.5fr) 132px minmax(140px,1fr) minmax(220px,1.4fr) 132px 104px 40px', height: 32, padding: '0 14px', fontSize: 10, borderBottom: '1px solid #DAD5CA' }}>
                  <span>Template</span><span>Status</span><span>Category</span><span>Description</span><span>Checklist</span><span>Updated</span><span />
                </div>
                {filtered.map(t => {
                  const draft = isDraft(t); const pc = photoCount(t);
                  return (
                    <div key={t.id} onClick={() => setDrawer(t)} data-testid={`template-row-${t.id}`} className="grid items-center cursor-pointer transition-colors hover:bg-[#FFFCEF]" style={{ gridTemplateColumns: 'minmax(250px,1.5fr) 132px minmax(140px,1fr) minmax(220px,1.4fr) 132px 104px 40px', padding: '9px 14px', borderBottom: '1px solid #F0EDE6', background: draft ? '#FFFDF5' : undefined }}>
                      <div className="min-w-0 pr-2">
                        <div className="font-semibold text-[#1A1A1A] truncate" style={{ fontSize: 13 }}>{t.name}</div>
                        {t.source_standard_code && <div className="font-mono text-[#8F8A7F]" style={{ fontSize: 10.5 }}>From {t.source_standard_code}</div>}
                      </div>
                      <div><StatusPill template={t} /></div>
                      <span className="text-[#3D3A35] truncate" style={{ fontSize: 12.5 }}>{t.category || '—'}</span>
                      <span className="text-[#6B6660] truncate pr-2" style={{ fontSize: 12.5 }}>{t.description || '—'}</span>
                      <span style={{ fontSize: 12.5 }}><span className="font-mono font-semibold text-[#1A1A1A]">{(t.checklist || []).length}</span> <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>items{pc > 0 ? ` · ${pc} photo` : ''}</span></span>
                      <span className="font-mono text-[#6B6660]" style={{ fontSize: 11.5 }}>{relTime(t.updated_at)}</span>
                      <ChevronRight className="w-[15px] h-[15px]" style={{ color: '#B5AFA3' }} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        <div className="flex items-center justify-between" style={{ padding: '11px 14px', borderTop: '1px solid #E6E2D9', background: '#FBFAF7' }}>
          <span className="text-[#6B6660]" style={{ fontSize: 12 }}>Showing {filtered.length} of {templates.length} templates</span>
          <span className="text-[#8F8A7F]" style={{ fontSize: 11.5 }}>{statusCounts.draft} draft · {statusCounts.published} live in the field</span>
        </div>
      </div>

      {/* Detail drawer */}
      <SlideDrawer open={!!drawer} onOpenChange={(o) => { if (!o) setDrawer(null); }} title="Template" dismissable
        titleExtra={drawer && <span className="font-mono text-[#8F8A7F]" style={{ fontSize: 11 }}>v{drawer.version || 1}</span>} size="lg">
        {drawer && <DetailBody template={drawer} formatDate={formatDate}
          onEdit={() => { const t = drawer; setDrawer(null); setModal({ existing: t }); }}
          onDuplicate={() => handleDuplicate(drawer)} onPublish={() => handlePublish(drawer)} onDelete={() => handleDelete(drawer)} />}
      </SlideDrawer>

      {/* Create / edit modal */}
      {modal && <TemplateFormModal existing={modal.existing} onClose={() => setModal(null)} onSuccess={() => { setModal(null); loadTemplates(); }} />}

      {/* Generate */}
      {generateOpen && <GenerateModal onClose={() => setGenerateOpen(false)} onGenerated={(tpl) => { setGenerateOpen(false); loadTemplates(tpl?.id); }} />}

      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

export default TemplatesListPage;
